import html2pdf from "html2pdf.js";
import { FieldProps, FieldVariant, SectionVariant } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import {
  WizardComponentMap,
  WizardSharedData,
} from "../../reusable_components/WizardPageController/WizardPageController.utils";
import AcceptanceApprovalViewer from "./components/AcceptanceApprovalViewer/AcceptanceApprovalViewer";
import BackgroundApprovalViewer from "./components/BackgroundApprovalViewer/BackgroundApprovalViewer";
import OnboardingApprovalViewer from "./components/OnboardingApprovalViewer/OnboardingApprovalViewer";
import AttestApprovalViewer from "./components/AttestApprovalViewer/AttestApprovalViewer";
import { ClientRecord } from "../ClientsGrid/ClientsGrid.utils";

export interface WizardPagesProps {
  data: any;
  flag: boolean;
  id: any;
  pageName: any;
}

export interface ClientApprovalWizardSharedData extends WizardSharedData {
  clientMasterId: number;
  attestFormId: number;
  onboardingFormId: number;
  taxAcceptanceFormId: number;
  auditAcceptanceFormId: number;
  firmDetailId: number;
  onboardingFormData: FieldProps[];
  onboardingFieldVariant?: FieldVariant;
  onboardingSectionVariant?: SectionVariant;
  attestFieldVariant?: FieldVariant;
  attestSectionVariant?: SectionVariant;
  acceptanceFormData: FieldProps[];
  acceptanceFieldVariant?: FieldVariant;
  acceptanceSectionVariant?: SectionVariant;
  isPublic: boolean;
  selectedPurposes: any[];
  fileInputs: FileInputTypes[];
  onboardingFeedback: string;
  acceptanceFeedback: string;
  backgroundFeedback: string;
  approvalAPIProgress?:boolean;
}

export const clientApprovalComponentMap: WizardComponentMap<ClientApprovalWizardSharedData> =
  {
    CLIENT_ATTEST: AttestApprovalViewer,
    CLIENT_ONBOARDING: OnboardingApprovalViewer,
    CLIENT_TAX_ACCEPTANCE: AcceptanceApprovalViewer,
    CLIENT_AUDIT_ACCEPTANCE: AcceptanceApprovalViewer,
    DOCUMENTS: BackgroundApprovalViewer,
  };

export interface FileInputTypes {
  FirmReferenceDataId: number;
  ReferenceDataCode: string;
  ReferenceDataDescription: string;
  ReferenceDataDomain: string;
  ReferenceDataValue: string;
  CreatedDate: string;
  CreatedBy: string;
}

export type ClientApprovalWizardProps = {
  show: boolean;
  onHide: () => void;
  clientMasterId: number;
  attestFormId: number;
  onboardingFormId: number;
  taxAcceptanceFormId: number;
  auditAcceptanceFormId: number;
  firmDetailId: number;
  clientRecord: ClientRecord;
  rejectOptions: object;
  refresh: () => void;
};

// Add these interfaces (or import from utils)
export interface ApproverData {
  ClientMasterId: number;
  ClientId: string;
  ClientName: string;
  CreatedBy: string;
  CreatedDate: string;
  UpdatedDate: string;
  ApprovalStatus: string;
  UserFullName: string;
  ApproverOrder: number;
  Active: string;
  FirmDetailId: number;
  // ... other fields as needed
}

/**
 * Utility function to download the scrollable wizard content as a PDF.
 *
 * @param scrollableContentRef - Ref to the DOM element to convert into PDF
 */
export const handleDownloadPDF = (scrollableContentRef: { current: any }) => {
  if (scrollableContentRef.current) {
    const element = scrollableContentRef.current;

    html2pdf()
      .from(element)
      .set({
        margin: 0.5,
        filename: "ClientApprovalDetails.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      })
      .save();
  }
};


// put this outside the component (so it’s stable)
export const hasUsableId = (v: unknown): boolean => {
  const s = String(v ?? "").trim();
  return s !== "" && s.toUpperCase() !== "N/A" && s !== "0";
};

// optional: if your fetch wants a number
export const toNumericId = (v: unknown): number | undefined => {
  if (!hasUsableId(v)) return undefined;
  const n = Number(String(v).trim());
  return Number.isFinite(n) && n > 0 ? n : undefined;
};


/**
 * Formats current timestamp in EST timezone with detailed format.
 * Format: DD/MM/YYYY HH:mm:ss EST
 */
export const formatApprovalTimestamp = (): string => {
  const now = new Date();
  
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/New_York',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  // Format: "DD/MM/YYYY, HH:mm:ss"
  const formatted = formatter.format(now);
  
  return `${formatted} EST`;
};